import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area,
} from "recharts";
import {
  ChartPie, TrendUp, CurrencyDollar, Airplane, FilePdf,
  CaretLeft, CaretRight, MapPin, Buildings, CalendarBlank, FunnelSimple,
} from "@phosphor-icons/react";

const fmt = (n) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 0 }).format(n || 0);

const PIE_COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#6366F1", "#F97316"];
const MONTH_NAMES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

const CAT_LABELS = {
  alimentos: "Alimentos", hospedaje: "Hospedaje", casetas: "Casetas",
  autobus: "Autobus", vuelos: "Vuelos", gasolina: "Gasolina",
  incidentes: "Incidentes", otros: "Otros",
};

const STATUS_LABELS = {
  solicitado: "Solicitado", aprobado: "Aprobado", rechazado: "Rechazado",
  en_viaje: "En Viaje", en_liquidacion: "En Liquidacion",
  liquidado: "Liquidado", cerrado: "Cerrado",
};

const STATUS_COLORS_MAP = {
  solicitado: "#3B82F6", aprobado: "#10B981", rechazado: "#EF4444",
  en_viaje: "#F59E0B", en_liquidacion: "#8B5CF6",
  liquidado: "#14B8A6", cerrado: "#64748B",
};

const PERIODS = [
  { id: "monthly", label: "Mensual" },
  { id: "biweekly", label: "Quincenal" },
  { id: "weekly", label: "Semanal" },
  { id: "all", label: "Todo el Ano" },
];

function pad(n) { return String(n).padStart(2, "0"); }
function fmtDateISO(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }

function calcRange(period, year, offset) {
  const now = new Date();
  const curMonth = now.getMonth() + 1;

  if (period === "all") {
    return { start: `${year}-01-01`, end: `${year}-12-31`, label: `${year}` };
  }

  if (period === "monthly") {
    let m = curMonth + offset;
    let y = year;
    while (m > 12) { m -= 12; y++; }
    while (m < 1) { m += 12; y--; }
    const lastDay = new Date(y, m, 0).getDate();
    return {
      start: `${y}-${pad(m)}-01`,
      end: `${y}-${pad(m)}-${lastDay}`,
      label: `${MONTH_NAMES[m - 1]} ${y}`,
    };
  }

  if (period === "biweekly") {
    const baseHalf = now.getDate() <= 15 ? 0 : 1;
    const baseIndex = (curMonth - 1) * 2 + baseHalf + offset;
    const clampedIndex = Math.max(0, Math.min(23, baseIndex));
    const m = Math.floor(clampedIndex / 2) + 1;
    const h = (clampedIndex % 2) + 1;
    const lastDay = new Date(year, m, 0).getDate();
    if (h === 1) {
      return { start: `${year}-${pad(m)}-01`, end: `${year}-${pad(m)}-15`, label: `1-15 ${MONTH_NAMES[m - 1]} ${year}` };
    }
    return { start: `${year}-${pad(m)}-16`, end: `${year}-${pad(m)}-${lastDay}`, label: `16-${lastDay} ${MONTH_NAMES[m - 1]} ${year}` };
  }

  if (period === "weekly") {
    const ref = new Date(year, now.getMonth(), now.getDate());
    const day = ref.getDay() || 7;
    const monday = new Date(ref);
    monday.setDate(ref.getDate() - day + 1 + offset * 7);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return {
      start: fmtDateISO(monday),
      end: fmtDateISO(sunday),
      label: `${monday.getDate()} ${MONTH_NAMES[monday.getMonth()]} - ${sunday.getDate()} ${MONTH_NAMES[sunday.getMonth()]} ${sunday.getFullYear()}`,
    };
  }

  return { start: `${year}-01-01`, end: `${year}-12-31`, label: `${year}` };
}

function KpiCard({ icon: Icon, label, value, sub, color = "blue" }) {
  const colorMap = {
    blue: "from-blue-500 to-blue-600", green: "from-emerald-500 to-emerald-600",
    amber: "from-amber-500 to-amber-600", purple: "from-purple-500 to-purple-600",
    teal: "from-teal-500 to-teal-600", slate: "from-slate-500 to-slate-600",
  };
  return (
    <div className={`bg-gradient-to-br ${colorMap[color] || colorMap.blue} rounded-xl p-4 text-white shadow-lg`} data-testid={`viaticos-kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 mb-2 opacity-90">
        <Icon size={18} weight="duotone" />
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
      {sub && <p className="text-xs opacity-75 mt-1">{sub}</p>}
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="flex justify-between gap-4">
          <span>{p.name}:</span>
          <span className="font-semibold">{typeof p.value === "number" ? fmt(p.value) : p.value}</span>
        </p>
      ))}
    </div>
  );
}

export function ViaticosAdminDashboard({ onBack }) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [period, setPeriod] = useState("monthly");
  const [offset, setOffset] = useState(0);

  const headers = { Authorization: `Bearer ${token}` };

  const range = useMemo(() => calcRange(period, year, offset), [period, year, offset]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/viaticos/dashboard?year=${year}`;
      if (period !== "all") {
        url += `&start_date=${range.start}&end_date=${range.end}`;
      }
      const { data: d } = await axios.get(url, { headers });
      setData(d);
    } catch {
      toast.error("Error cargando dashboard de viaticos");
    }
    setLoading(false);
  }, [token, year, range.start, range.end, period]);

  useEffect(() => { load(); }, [load]);

  const changePeriod = (p) => { setPeriod(p); setOffset(0); };
  const changeYear = (delta) => { setYear(y => y + delta); setOffset(0); };

  const handleExportPdf = () => {
    let url = `${API}/viaticos/dashboard/pdf?year=${year}&token=${token}`;
    if (period !== "all") {
      url += `&start_date=${range.start}&end_date=${range.end}`;
    }
    window.open(url, "_blank");
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-16 text-slate-500">
        <ChartPie size={48} className="mx-auto mb-3 text-slate-300" />
        <p className="font-medium">No se pudieron cargar los datos</p>
      </div>
    );
  }

  // Prepare chart data
  const tripsMonthData = Array.from({ length: 12 }, (_, i) => {
    const key = `${year}-${String(i + 1).padStart(2, "0")}`;
    return { name: MONTH_NAMES[i], viajes: data.trips_by_month[key] || 0 };
  });

  const quarterData = ["Q1", "Q2", "Q3", "Q4"].map(q => ({
    name: q, viajes: data.trips_by_quarter[q] || 0,
  }));

  const categoryPieData = Object.entries(data.total_by_category || {}).map(([k, v]) => ({
    name: CAT_LABELS[k] || k, value: Math.round(v * 100) / 100,
  })).sort((a, b) => b.value - a.value);

  const monthlyCatData = Array.from({ length: 12 }, (_, i) => {
    const key = `${year}-${String(i + 1).padStart(2, "0")}`;
    const cats = data.monthly_by_category[key] || {};
    return { name: MONTH_NAMES[i], ...Object.fromEntries(Object.entries(cats).map(([k, v]) => [CAT_LABELS[k] || k, v])) };
  });

  const statusPieData = Object.entries(data.status_distribution || {}).map(([k, v]) => ({
    name: STATUS_LABELS[k] || k, value: v, fill: STATUS_COLORS_MAP[k] || "#94a3b8",
  }));

  const visitTypeData = Object.entries(data.by_visit_type || {}).map(([k, v]) => ({
    name: k, viajes: v,
  })).sort((a, b) => b.viajes - a.viajes);

  const clientData = (data.avg_by_client || []).slice(0, 10);
  const allCategories = [...new Set(Object.values(data.monthly_by_category || {}).flatMap(m => Object.keys(m)))];

  return (
    <div className="space-y-5" data-testid="viaticos-dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-testid="viaticos-dashboard-back">
            <CaretLeft size={20} className="text-slate-500" />
          </button>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Dashboard de Viaticos</h2>
            <p className="text-sm text-slate-500">Analisis y metricas del modulo de viaticos</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => changeYear(-1)} className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid="viaticos-dash-prev-year">
            <CaretLeft size={18} />
          </button>
          <span className="text-lg font-bold text-slate-700 min-w-[60px] text-center" data-testid="viaticos-dash-year">{year}</span>
          <button onClick={() => changeYear(1)} className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid="viaticos-dash-next-year">
            <CaretRight size={18} />
          </button>
          <button onClick={handleExportPdf} className="ml-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 flex items-center gap-1.5 shadow-sm" data-testid="viaticos-dash-export-pdf">
            <FilePdf size={16} weight="duotone" /> Exportar PDF
          </button>
        </div>
      </div>

      {/* Period Filter */}
      <div className="flex flex-wrap items-center gap-3 bg-white rounded-xl border p-3 shadow-sm" data-testid="viaticos-period-filter">
        <FunnelSimple size={16} className="text-slate-400" />
        <div className="flex gap-0.5 bg-slate-100 p-0.5 rounded-lg">
          {PERIODS.map(p => (
            <button key={p.id} onClick={() => changePeriod(p.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${period === p.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              data-testid={`viaticos-period-${p.id}`}>
              {p.label}
            </button>
          ))}
        </div>
        {period !== "all" && (
          <div className="flex items-center gap-1">
            <button onClick={() => setOffset(o => o - 1)} className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid="viaticos-period-prev">
              <CaretLeft size={14} className="text-slate-500" />
            </button>
            <span className="text-xs font-medium text-slate-600 bg-slate-50 border px-3 py-1.5 rounded-lg min-w-[140px] text-center flex items-center justify-center gap-1.5" data-testid="viaticos-period-label">
              <CalendarBlank size={13} className="text-slate-400" />
              {range.label}
            </span>
            <button onClick={() => setOffset(o => o + 1)} className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid="viaticos-period-next">
              <CaretRight size={14} className="text-slate-500" />
            </button>
          </div>
        )}
        {period === "all" && (
          <span className="text-xs text-slate-500">Mostrando todo el ano {year}</span>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard icon={Airplane} label="Solicitudes" value={data.total_solicitudes} sub={`${data.total_approved} aprobadas`} color="blue" />
        <KpiCard icon={CurrencyDollar} label="Solicitado" value={fmt(data.total_requested)} color="amber" />
        <KpiCard icon={TrendUp} label="Aprobado" value={fmt(data.total_approved_amount)} color="green" />
        <KpiCard icon={CurrencyDollar} label="Gastado" value={fmt(data.total_spent)} color="purple" />
        <KpiCard icon={MapPin} label="Tipos Visita" value={Object.keys(data.by_visit_type || {}).length} sub="categorias activas" color="teal" />
        <KpiCard icon={Buildings} label="Empresas" value={(data.avg_by_client || []).length} sub="con gastos registrados" color="slate" />
      </div>

      {/* Row 1: Trips by month + Quarter */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-xl border p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Viajes por Mes</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={tripsMonthData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="viajes" name="Viajes" fill="#3B82F6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl border p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Viajes por Trimestre</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={quarterData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="viajes" name="Viajes" fill="#10B981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Row 2: Category pie + Status pie */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Gasto Total por Categoria</h3>
          {categoryPieData.length === 0 ? (
            <p className="text-center text-slate-400 py-12 text-sm">Sin datos en este periodo</p>
          ) : (
            <div className="flex items-center">
              <ResponsiveContainer width="60%" height={240}>
                <PieChart>
                  <Pie data={categoryPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={45} paddingAngle={2}>
                    {categoryPieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmt(v)} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-1.5 text-xs">
                {categoryPieData.map((c, i) => (
                  <div key={c.name} className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                    <span className="text-slate-600 truncate">{c.name}</span>
                    <span className="ml-auto font-semibold text-slate-800">{fmt(c.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Distribucion por Estatus</h3>
          {statusPieData.length === 0 ? (
            <p className="text-center text-slate-400 py-12 text-sm">Sin datos en este periodo</p>
          ) : (
            <div className="flex items-center">
              <ResponsiveContainer width="60%" height={240}>
                <PieChart>
                  <Pie data={statusPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={45} paddingAngle={2}>
                    {statusPieData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-1.5 text-xs">
                {statusPieData.map((s) => (
                  <div key={s.name} className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: s.fill }} />
                    <span className="text-slate-600 truncate">{s.name}</span>
                    <span className="ml-auto font-semibold text-slate-800">{s.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Row 3: Monthly expense by category (stacked area) */}
      <div className="bg-white rounded-xl border p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-700 mb-4">Gasto Mensual por Categoria</h3>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={monthlyCatData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {allCategories.map((cat, i) => (
              <Area key={cat} type="monotone" dataKey={CAT_LABELS[cat] || cat} stackId="1" stroke={PIE_COLORS[i % PIE_COLORS.length]} fill={PIE_COLORS[i % PIE_COLORS.length]} fillOpacity={0.6} />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Row 4: Visit type bar + Client avg table */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Viajes por Tipo de Visita</h3>
          {visitTypeData.length === 0 ? (
            <p className="text-center text-slate-400 py-12 text-sm">Sin datos en este periodo</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={visitTypeData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
                <Tooltip />
                <Bar dataKey="viajes" name="Viajes" fill="#8B5CF6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-xl border p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Gasto Promedio Mensual por Empresa</h3>
          {clientData.length === 0 ? (
            <p className="text-center text-slate-400 py-12 text-sm">Sin datos de gastos en este periodo</p>
          ) : (
            <div className="overflow-auto max-h-[280px]">
              <table className="w-full text-xs" data-testid="viaticos-dash-client-table">
                <thead className="sticky top-0">
                  <tr className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                    <th className="text-left py-2 px-3 font-semibold">Empresa</th>
                    <th className="text-right py-2 px-3 font-semibold">Total</th>
                    <th className="text-right py-2 px-3 font-semibold">Meses</th>
                    <th className="text-right py-2 px-3 font-semibold">Promedio</th>
                  </tr>
                </thead>
                <tbody>
                  {clientData.map((c, i) => (
                    <tr key={c.client || i} className="border-t hover:bg-slate-50/70">
                      <td className="py-2 px-3 text-slate-700 font-medium truncate max-w-[160px]">{c.client}</td>
                      <td className="py-2 px-3 text-right text-slate-600">{fmt(c.total)}</td>
                      <td className="py-2 px-3 text-right text-slate-500">{c.months}</td>
                      <td className="py-2 px-3 text-right font-semibold text-slate-800">{fmt(c.average)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Row 5: Expense by User */}
      <div className="bg-white rounded-xl border p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-700 mb-4">Gasto por Usuario</h3>
        {(data.expense_by_user || []).length === 0 ? (
          <p className="text-center text-slate-400 py-8 text-sm">Sin datos en este periodo</p>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-xs" data-testid="viaticos-dash-user-table">
              <thead>
                <tr className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                  <th className="text-left py-2.5 px-3 font-semibold">Usuario</th>
                  <th className="text-left py-2.5 px-3 font-semibold">Departamento</th>
                  <th className="text-right py-2.5 px-3 font-semibold">Viajes</th>
                  <th className="text-right py-2.5 px-3 font-semibold">Solicitado</th>
                  <th className="text-right py-2.5 px-3 font-semibold">Gastado</th>
                </tr>
              </thead>
              <tbody>
                {data.expense_by_user.map((u, i) => (
                  <tr key={i} className="border-t hover:bg-slate-50/70">
                    <td className="py-2 px-3 text-slate-700 font-medium">{u.name}</td>
                    <td className="py-2 px-3 text-slate-500">{u.department || "—"}</td>
                    <td className="py-2 px-3 text-right text-slate-600">{u.trips}</td>
                    <td className="py-2 px-3 text-right text-slate-600">{fmt(u.requested)}</td>
                    <td className="py-2 px-3 text-right font-semibold text-slate-800">{fmt(u.spent)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Row 6: Expense by Department */}
      <div className="bg-white rounded-xl border p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-700 mb-4">Departamento con Mayor Gasto</h3>
        {(data.expense_by_department || []).length === 0 ? (
          <p className="text-center text-slate-400 py-8 text-sm">Sin datos en este periodo</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.expense_by_department} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="department" tick={{ fontSize: 11 }} width={120} />
                <Tooltip formatter={(v) => fmt(v)} />
                <Bar dataKey="spent" name="Gastado" fill="#EF4444" radius={[0, 4, 4, 0]} />
                <Bar dataKey="requested" name="Solicitado" fill="#93c5fd" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div className="overflow-auto">
              <table className="w-full text-xs" data-testid="viaticos-dash-dept-table">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                    <th className="text-left py-2.5 px-3 font-semibold">Departamento</th>
                    <th className="text-right py-2.5 px-3 font-semibold">Viajes</th>
                    <th className="text-right py-2.5 px-3 font-semibold">Solicitado</th>
                    <th className="text-right py-2.5 px-3 font-semibold">Gastado</th>
                  </tr>
                </thead>
                <tbody>
                  {data.expense_by_department.map((d, i) => (
                    <tr key={i} className={`border-t hover:bg-slate-50/70 ${i === 0 ? "bg-red-50" : ""}`}>
                      <td className="py-2 px-3 text-slate-700 font-medium">{d.department}{i === 0 && <span className="ml-1.5 text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-semibold">Mayor gasto</span>}</td>
                      <td className="py-2 px-3 text-right text-slate-600">{d.trips}</td>
                      <td className="py-2 px-3 text-right text-slate-600">{fmt(d.requested)}</td>
                      <td className="py-2 px-3 text-right font-semibold text-slate-800">{fmt(d.spent)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Row 7: Detailed Solicitudes Table */}
      <div className="bg-white rounded-xl border p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-700 mb-4">Detalle de Solicitudes</h3>
        {(data.solicitudes_detail || []).length === 0 ? (
          <p className="text-center text-slate-400 py-8 text-sm">Sin solicitudes en este periodo</p>
        ) : (
          <div className="overflow-auto max-h-[420px]">
            <table className="w-full text-xs" data-testid="viaticos-dash-detail-table">
              <thead className="sticky top-0 z-10">
                <tr className="bg-slate-800 text-white">
                  <th className="text-left py-2.5 px-3 font-semibold">ID</th>
                  <th className="text-left py-2.5 px-3 font-semibold">Usuario</th>
                  <th className="text-left py-2.5 px-3 font-semibold">Destino</th>
                  <th className="text-left py-2.5 px-3 font-semibold">Empresas Visitadas</th>
                  <th className="text-left py-2.5 px-3 font-semibold">Tipo</th>
                  <th className="text-left py-2.5 px-3 font-semibold">Fechas</th>
                  <th className="text-right py-2.5 px-3 font-semibold">Solicitado</th>
                  <th className="text-right py-2.5 px-3 font-semibold">Gastado</th>
                  <th className="text-center py-2.5 px-3 font-semibold">Estatus</th>
                </tr>
              </thead>
              <tbody>
                {data.solicitudes_detail.map((s, i) => (
                  <tr key={i} className="border-t hover:bg-slate-50/70">
                    <td className="py-2 px-3 text-blue-600 font-mono font-medium">{s.id}</td>
                    <td className="py-2 px-3 text-slate-700">{s.user_name}</td>
                    <td className="py-2 px-3 text-slate-600">{s.location || "—"}</td>
                    <td className="py-2 px-3 text-slate-600 max-w-[180px] truncate">{s.clients}</td>
                    <td className="py-2 px-3 text-slate-500">{s.visit_type}</td>
                    <td className="py-2 px-3 text-slate-500 whitespace-nowrap">{s.date_start} → {s.date_end}</td>
                    <td className="py-2 px-3 text-right text-slate-600">{fmt(s.requested)}</td>
                    <td className="py-2 px-3 text-right font-semibold text-slate-800">{fmt(s.spent)}</td>
                    <td className="py-2 px-3 text-center">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        s.status === "aprobado" ? "bg-emerald-100 text-emerald-700" :
                        s.status === "rechazado" ? "bg-red-100 text-red-700" :
                        s.status === "cerrado" ? "bg-slate-200 text-slate-600" :
                        s.status === "liquidado" ? "bg-teal-100 text-teal-700" :
                        s.status === "en_viaje" ? "bg-amber-100 text-amber-700" :
                        s.status === "en_liquidacion" ? "bg-purple-100 text-purple-700" :
                        "bg-blue-100 text-blue-700"
                      }`}>
                        {STATUS_LABELS[s.status] || s.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
